import { TrendingUp, Users, HardDrive } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import type { Tenant } from '@/lib/types'
import { getPlanLimits, formatBytes } from '@/features/tenants/lib/utils'

interface UsageData {
  projects: { used: number; limit: number; percentage: number }
  users: { used: number; limit: number; percentage: number }
  storage: { used: number; limit: number; percentage: number }
}

interface TenantUsageModalProps {
  tenant: Tenant
  onClose: () => void
}

export function TenantUsageModal({ tenant, onClose }: TenantUsageModalProps) {
  const limits = getPlanLimits(tenant)

  // Mock usage data - replace with actual API call
  const usageData: UsageData = {
    projects: { used: 3, limit: limits.maxProjects, percentage: 30 },
    users: { used: 7, limit: limits.maxUsers, percentage: 70 },
    storage: {
      used: 2_147_483_648, // 2GB
      limit: limits.maxStorageBytes,
      percentage: 40,
    },
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Resource Usage</CardTitle>
          <CardDescription>
            Current usage vs limits for {tenant.name}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Projects */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Projects
              </span>
              <span className="font-medium">
                {usageData.projects.used} / {usageData.projects.limit === -1 ? '∞' : usageData.projects.limit}
              </span>
            </div>
            <div className="h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 dark:bg-blue-400 transition-all"
                style={{ width: `${Math.min(usageData.projects.percentage, 100)}%` }}
              />
            </div>
          </div>

          {/* Users */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Users
              </span>
              <span className="font-medium">
                {usageData.users.used} / {usageData.users.limit === -1 ? '∞' : usageData.users.limit}
              </span>
            </div>
            <div className="h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  usageData.users.percentage > 80 ? 'bg-red-600 dark:bg-red-400' :
                    usageData.users.percentage > 50 ? 'bg-amber-600 dark:bg-amber-400' :
                      'bg-green-600 dark:bg-green-400'
                }`}
                style={{ width: `${Math.min(usageData.users.percentage, 100)}%` }}
              />
            </div>
          </div>

          {/* Storage */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <HardDrive className="h-4 w-4" />
                Storage
              </span>
              <span className="font-medium">
                {formatBytes(usageData.storage.used)} / {formatBytes(usageData.storage.limit)}
              </span>
            </div>
            <div className="h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  usageData.storage.percentage > 80 ? 'bg-red-600 dark:bg-red-400' :
                    usageData.storage.percentage > 50 ? 'bg-amber-600 dark:bg-amber-400' :
                      'bg-green-600 dark:bg-green-400'
                }`}
                style={{ width: `${Math.min(usageData.storage.percentage, 100)}%` }}
              />
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={onClose}
          >
            Close
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
